import classNames from 'classnames'
import { useEffect, useState } from 'react'
import BSCard from 'react-bootstrap/Card'
import BSNav from 'react-bootstrap/Nav'
import { useCookies } from 'react-cookie'
import { forceCheck } from 'react-lazyload'

import AdditionalFilters from '@/components/CardLists/AdditionalFilters/AdditionalFilters'
import cardService from '@/services/card.service'
import { initStore } from '@/store/cardlists/actions/initStore'
import { useAppSelector } from '@/store/hooks'
import { CardListType, PageCardType } from '@/types/page'

import styles from './CardLists.module.scss'
import CardView from './CardView/CardView'
import TableView from './TableView/TableView'
import TextView from './TextView/TextView'

type Props = {
  card?: PageCardType
  excludeScrollSpy?: boolean
  initCardLists?: CardListType[]
}

const CardLists = ({ card, excludeScrollSpy, initCardLists }: Props) => {
  const { cardLists: storeCardLists, updatedAt } = useAppSelector((state) => state.cardlists)
  const [cookies, setCookies] = useCookies(['cardListView'])
  const view = cookies.cardListView || 'card'
  // Track if additional filters are open, for styling purposes
  const [additionalFiltersIsOpen, setAdditionalFiltersIsOpen] = useState(false)

  // Recheck LazyLoad elements on window resize
  useEffect(() => {
    window.addEventListener('resize', forceCheck)
    return () => window.removeEventListener('resize', forceCheck)
  }, [])

  // Fetch full card data after initial render
  useEffect(() => {
    if (initCardLists) {
      initStore(initCardLists)

      const names: string[] = []
      initCardLists.forEach((cardlist) => {
        cardlist.cardviews.forEach((cardview) => {
          if (cardview.cards) cardview.cards.forEach((card) => names.push(card.url))
          else names.push(cardview.sanitized_wo)
        })
      })
      cardService.getCardMap(names)
    }
  }, [initCardLists])

  // Recheck LazyLoad elements on cardlist update
  useEffect(() => {
    if (updatedAt) forceCheck()
  }, [updatedAt])

  if (!initCardLists) return null

  const cardlists = updatedAt ? storeCardLists : initCardLists

  return (
    <div className='mvCardList'>
      <BSCard className={classNames(styles.container, 'shadow-sm')}>
        <div className={classNames(styles.header, { [styles.additionalFiltersIsOpen]: additionalFiltersIsOpen })}>
          <AdditionalFilters setIsOpen={setAdditionalFiltersIsOpen} />
          <div className='ms-sm-auto'>
            <BSNav activeKey={view} className={styles.buttons} variant='pills'>
              <BSNav.Item onClick={() => setCookies('cardListView', 'card')}>
                <BSNav.Link className='d-flex flex-row' eventKey='card'>
                  <span>Card</span>
                  <span className='d-none d-sm-block'>&nbsp;View</span>
                </BSNav.Link>
              </BSNav.Item>
              <BSNav.Item onClick={() => setCookies('cardListView', 'table')}>
                <BSNav.Link className='d-flex flex-row' eventKey='table'>
                  <span>Table</span>
                  <span className='d-none d-sm-block'>&nbsp;View</span>
                </BSNav.Link>
              </BSNav.Item>
              <BSNav.Item onClick={() => setCookies('cardListView', 'text')}>
                <BSNav.Link className='d-flex flex-row' eventKey='text'>
                  <span>Text</span>
                  <span className='d-none d-sm-block'>&nbsp;View</span>
                </BSNav.Link>
              </BSNav.Item>
            </BSNav>
          </div>
        </div>
        <div className={styles.body}>
          {view === 'card' && (
            <CardView excludeScrollSpy={excludeScrollSpy} cardLists={cardlists} pageCommander={card} />
          )}
          {view === 'text' && <TextView cardLists={cardlists} />}
          {view === 'table' && <TableView cardLists={cardlists} />}
        </div>
      </BSCard>
    </div>
  )
}

export default CardLists
